import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AIDrawerContext,
  CompanionSupportChannel,
  AIDrawerLayout,
  AIDrawerOptions,
  AIDrawerThreadTarget,
  CompanionShellMode,
} from './AIDrawerContext';
import {
  clampShellPosition,
  clampShellSize,
  clearShellPosition,
  clearShellSize,
  currentShellViewport,
  loadShellPosition,
  loadShellSize,
  maxShellSize,
  saveShellPosition,
  saveShellSize,
  type ShellPosition,
  type ShellSize,
  type Viewport,
} from '../components/ora/Global/CompanionShell/useDragPosition';
import { AUDIO_BAR_OFFSET_EVENT } from '../audioOverview/bar/audioBarOffset';
import useWindowWidth from '../hooks/useWindowWidth';
import { Capacitor } from '@capacitor/core';
import { showIntercomMessages, subscribeIntercom } from '../utils/intercom';

interface AIDrawerProviderProps {
  children: React.ReactNode;
}

const AI_DRAWER_PUSH_BREAKPOINT = 1270;

const AIDrawerProvider: React.FC<AIDrawerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  // The drawer is only reachable where `AIDrawerRenderer` actually mounts it,
  // and only where that surface does not suppress it. Both are reported by the
  // renderer (which lives inside the router) so every entry point in the
  // chrome — rail item, tab bar, `c` hotkey, body gutter class — agrees with
  // what is on screen.
  const [isMounted, setIsMounted] = useState(false);
  const [isSuppressed, setIsSuppressed] = useState(false);
  const [isWebinarStudyPhase, setIsWebinarStudyPhase] = useState(false);
  // A fullscreen page (a game round) asking the companion to stand down; see
  // `useOsWindowVariantOverride`.
  const [isPageSuppressed, setIsPageSuppressed] = useState(false);
  const [hasMobileLauncher, setHasMobileLauncher] = useState(false);
  const [hasMobileNavRow, setHasMobileNavRow] = useState(false);
  // Which renderer is on screen. Reported by `AIDrawerRenderer`, which is the
  // only place that knows whether this route gets the push drawer or the
  // floating shell (ORA-645).
  const [layout, setLayout] = useState<AIDrawerLayout>('drawer');
  const [isHotkeyEnabled, setIsHotkeyEnabled] = useState(true);

  // The shell's open modes. `isOpen` decides bubble vs. not, so this only
  // remembers which non-bubble mode to come back to.
  const [openShellMode, setOpenShellMode] = useState<'window' | 'expanded'>(
    'window'
  );

  const [supportChannel, setSupportChannel] =
    useState<CompanionSupportChannel>('ai');

  const [intercomUnreadCount, setIntercomUnreadCount] = useState(0);

  const [humanSupportError, setHumanSupportError] = useState<string | null>(
    null
  );

  // Null means the student never resized the window, which is what sends it
  // to the largest size the viewport holds (ORA-696).
  const [storedShellSize, setStoredShellSizeState] = useState<ShellSize | null>(
    () => {
      const stored = loadShellSize();
      return stored ? clampShellSize(stored) : null;
    }
  );

  // The default size follows the viewport, so it is tracked rather than read
  // once; the resize listener below keeps it current.
  const [shellViewport, setShellViewport] =
    useState<Viewport>(currentShellViewport);

  /**
   * The size the window renders at, resolved in one place so the panel, the
   * drag clamp and the resize gesture all agree: the size the student picked,
   * or the biggest aspect-locked window this viewport can hold.
   */
  const shellSize = useMemo(
    () => storedShellSize ?? maxShellSize(shellViewport),
    [shellViewport, storedShellSize]
  );

  const [shellPosition, setShellPositionState] = useState<ShellPosition | null>(
    () => {
      const stored = loadShellPosition();
      return stored
        ? clampShellPosition(stored, undefined, loadShellSize())
        : null;
    }
  );

  // An `open` that named a conversation (coming back from a study set). Held
  // here rather than in the contents so it survives the drawer mounting, and
  // cleared by whoever acts on it.
  const [threadTarget, setThreadTarget] = useState<AIDrawerThreadTarget | null>(
    null
  );

  const windowWidth = useWindowWidth();

  const isPushModeAvailable =
    !Capacitor.isNativePlatform() && windowWidth > AI_DRAWER_PUSH_BREAKPOINT;

  const isPushModeActive = isOpen && isPushModeAvailable;

  const open = useCallback((options: AIDrawerOptions = {}) => {
    if (options.conversationId) {
      const { conversationId, scrollToBlockId } = options;

      setThreadTarget((current) => ({
        conversationId,
        scrollToBlockId: scrollToBlockId ?? null,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    }

    setIsOpen(true);
  }, []);

  const clearThreadTarget = useCallback(() => setThreadTarget(null), []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const shellMode: CompanionShellMode = isOpen ? openShellMode : 'bubble';

  // Reopening always comes back as the window. Coming back from the bubble
  // straight into a full-screen overlay — from a nav-rail click, say — would
  // be a bigger jump than the student asked for.
  useEffect(() => {
    if (!isOpen) setOpenShellMode('window');
  }, [isOpen]);

  const setShellMode = useCallback(
    (mode: CompanionShellMode) => {
      if (mode === 'bubble') {
        close();
        return;
      }

      setOpenShellMode(mode);
      setIsOpen(true);
    },
    [close]
  );

  /**
   * Intercom owns the corner while its Messenger is up, so our shell gets out
   * of the way. `onShow` covers every route in: the header's "Human →", and
   * the "Contact support" buttons scattered through the app that call
   * `showNewIntercomMessage` directly.
   */
  useEffect(() => {
    const unsubscribes = [
      subscribeIntercom('onShow', () => {
        setSupportChannel('human');
        setHumanSupportError(null);
        close();
      }),
      subscribeIntercom('onHide', () => {
        // The shell stays minimized: closing Intercom returns the student to
        // the page, with the bubble back in the corner to reopen the thread.
        setSupportChannel('ai');
      }),
      subscribeIntercom('onUnreadCountChange', (count) => {
        setIntercomUnreadCount(count);
      }),
    ];

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [close]);

  /**
   * Optimistically minimize — the Messenger normally appears in the same
   * instant — but claim nothing about the channel until Intercom says so. If it
   * never opens, the window comes straight back with the reason, so the student
   * is never left staring at a page where both panels have gone.
   */
  const openHumanSupport = useCallback(() => {
    setHumanSupportError(null);
    close();

    void showIntercomMessages()
      .catch(() => false)
      .then((didOpen) => {
        if (didOpen) return;

        setHumanSupportError(
          'Support chat could not open. Open Help → Contact support instead.'
        );

        setOpenShellMode('window');
        setIsOpen(true);
      });
  }, [close]);

  const setShellPosition = useCallback(
    (position: ShellPosition) => {
      const clamped = clampShellPosition(position, undefined, shellSize);
      setShellPositionState(clamped);
      saveShellPosition(clamped);
    },
    [shellSize]
  );

  const setShellSize = useCallback((size: ShellSize) => {
    const clamped = clampShellSize(size);
    setStoredShellSizeState(clamped);
    saveShellSize(clamped);
  }, []);

  const resetShellPosition = useCallback(() => {
    setShellPositionState(null);
    setStoredShellSizeState(null);
    clearShellPosition();
    clearShellSize();
  }, []);

  // A monitor change or a resized window can leave a stored position off
  // screen, where the header — the only drag handle — is out of reach, or a
  // stored size larger than the viewport now allows.
  useEffect(() => {
    const handleResize = () => {
      let clampedSize: ShellSize | null = null;

      setShellViewport(currentShellViewport());

      setStoredShellSizeState((current) => {
        clampedSize = current ? clampShellSize(current) : current;
        return clampedSize;
      });

      setShellPositionState((current) =>
        current ? clampShellPosition(current, undefined, clampedSize) : current
      );
    };

    window.addEventListener('resize', handleResize);
    // The docked Audio Overview bar shrinks the window's area (ORA-735).
    window.addEventListener(AUDIO_BAR_OFFSET_EVENT, handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(AUDIO_BAR_OFFSET_EVENT, handleResize);
    };
  }, []);

  const isAvailable = isMounted && !isSuppressed && !isPageSuppressed;

  useEffect(() => {
    if (isOpen && !isAvailable) {
      close();
    }
  }, [close, isAvailable, isOpen]);

  useEffect(() => {
    document.body.classList.toggle(
      'ai-drawer-available',
      isAvailable && layout === 'drawer'
    );

    return () => {
      document.body.classList.remove('ai-drawer-available');
    };
  }, [isAvailable, layout]);

  useEffect(() => {
    document.body.classList.toggle(
      'ai-drawer-push-open',
      isPushModeActive && layout === 'drawer'
    );

    return () => {
      document.body.classList.remove('ai-drawer-push-open');
    };
  }, [isPushModeActive, layout]);

  // The shell floats over the page in the same corner other fixed chrome
  // uses, so the page has to know it is there. Ora's support button now stands
  // down entirely on the shell (ORA-646), so nothing styles off these today;
  // they stay as the layout signal any future fixed element needs, and as what
  // the renderer tests assert the layout by.
  const isShellLayoutActive = isAvailable && layout === 'shell';

  useEffect(() => {
    document.body.classList.toggle(
      'companion-shell-active',
      isShellLayoutActive
    );

    return () => {
      document.body.classList.remove('companion-shell-active');
    };
  }, [isShellLayoutActive]);

  useEffect(() => {
    document.body.classList.toggle(
      'companion-shell-open',
      isShellLayoutActive && isOpen
    );

    return () => {
      document.body.classList.remove('companion-shell-open');
    };
  }, [isOpen, isShellLayoutActive]);

  const value = useMemo(
    () => ({
      open,
      close,
      setIsMounted,
      setIsSuppressed,
      isWebinarStudyPhase,
      setIsWebinarStudyPhase,
      setIsPageSuppressed,
      isPageSuppressed,
      hasMobileLauncher,
      setHasMobileLauncher,
      hasMobileNavRow,
      setHasMobileNavRow,
      setLayout,
      layout,
      isHotkeyEnabled,
      setIsHotkeyEnabled,
      shellMode,
      setShellMode,
      shellPosition,
      setShellPosition,
      shellSize,
      setShellSize,
      resetShellPosition,
      supportChannel,
      openHumanSupport,
      humanSupportError,
      intercomUnreadCount,
      isOpen,
      isAvailable,
      threadTarget,
      clearThreadTarget,
      isPushModeAvailable,
      isPushModeActive,
    }),
    [
      open,
      close,
      isWebinarStudyPhase,
      isPageSuppressed,
      hasMobileLauncher,
      hasMobileNavRow,
      layout,
      isHotkeyEnabled,
      setIsHotkeyEnabled,
      shellMode,
      setShellMode,
      shellPosition,
      setShellPosition,
      shellSize,
      setShellSize,
      resetShellPosition,
      supportChannel,
      openHumanSupport,
      humanSupportError,
      intercomUnreadCount,
      isOpen,
      isAvailable,
      threadTarget,
      clearThreadTarget,
      isPushModeAvailable,
      isPushModeActive,
    ]
  );

  return (
    <AIDrawerContext.Provider value={value}>
      {children}
    </AIDrawerContext.Provider>
  );
};

export default AIDrawerProvider;
